import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuthStore } from '@/store/authStore';
import { ProtectedRoute } from './ProtectedRoute';

import { LoginPage } from '@/pages/LoginPage';
import { MiCarnetPage } from '@/pages/MiCarnetPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { MantenimientoPage } from '@/pages/MantenimientoPage';
import { InventarioPage } from '@/pages/InventarioPage';
import { AccesosPage } from '@/pages/AccesosPage';
import { SyncPage } from '@/pages/SyncPage';
import { UsuariosPage } from '@/pages/UsuariosPage';
import { EmpleadosPage } from '@/pages/EmpleadosPage';
import { SinPermisoPage } from '@/pages/SinPermisoPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Mapa de rutas del panel web.
 *
 * Estrategia:
 *  - Login NO usa AppLayout (pantalla completa).
 *  - Todo lo demás vive dentro de ProtectedRoute → AppLayout → Outlet.
 *  - Cada subgrupo (admin) puede restringirse por rol con `allowedRoles`.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },

  // Portal del empleado: SSO con Google, fuera de ProtectedRoute y de
  // AppLayout. Quien entra aqui no existe en usuarios_sistema y su token
  // lleva audiencia propia, asi que no alcanza ninguna ruta del panel.
  { path: '/mi-carnet', element: <MiCarnetPage /> },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <Inicio /> },
          { path: '/sin-permiso', element: <SinPermisoPage /> },

          {
            // Mantenimiento → solo ADMIN y SOPORTE
            element: <ProtectedRoute allowedRoles={['ADMIN', 'SOPORTE']} />,
            children: [{ path: '/mantenimiento', element: <MantenimientoPage /> }],
          },

          // Inventario → ADMIN, SOPORTE, COMPRAS
          {
            element: <ProtectedRoute allowedRoles={['ADMIN', 'SOPORTE', 'COMPRAS']} />,
            children: [{ path: '/inventario', element: <InventarioPage /> }],
          },

          // Accesos y Empleados → ADMIN, SOPORTE y PEOPLE (los dos únicos
          // módulos del perfil de Gestión Humana)
          {
            element: <ProtectedRoute allowedRoles={['ADMIN', 'SOPORTE', 'PEOPLE']} />,
            children: [
              { path: '/accesos', element: <AccesosPage /> },
              { path: '/colaboradores', element: <EmpleadosPage /> },
            ],
          },

          // Administración → solo ADMIN
          {
            element: <ProtectedRoute allowedRoles={['ADMIN']} />,
            children: [
              { path: '/admin/sync', element: <SyncPage /> },
              { path: '/admin/usuarios', element: <UsuariosPage /> },
            ],
          },

          // Placeholders para futuras rutas
          { path: '/perfil',        element: <ComingSoon nombre="Mi perfil" /> },
        ],
      },
    ],
  },

  { path: '/404',  element: <NotFoundPage /> },
  { path: '*',     element: <Navigate to="/404" replace /> },
]);

/**
 * Pantalla de inicio. PEOPLE no tiene módulos en el dashboard (solo Empleados
 * y Accesos), así que aterriza directo en el directorio.
 */
function Inicio() {
  const rol = useAuthStore((s) => s.user?.rol);
  return rol === 'PEOPLE' ? <Navigate to="/colaboradores" replace /> : <DashboardPage />;
}

function ComingSoon({ nombre }: { nombre: string }) {
  return (
    <div className="min-h-[40vh] flex flex-col items-center justify-center text-center">
      <p className="text-xs font-medium uppercase tracking-widest text-primary-600 mb-2">Próximamente</p>
      <h2 className="text-2xl font-bold tracking-tight">{nombre}</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md">
        Esta sección se habilitará en el siguiente sprint.
      </p>
    </div>
  );
}
